import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { BuilderContent } from '@builder.io/sdk';
import { HreflangUrl } from '@/api/cms/cms';
import { useDamFile } from '@/contexts/DamFileProvider';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { SettingsResponse } from '@/generated/data.schemas';
import { PageMetadata } from '../routes/_head.partial';
import { getHreflangs } from './hreflang';

interface UsePageMetaDataOptions {
	skipCanonicalFallback?: boolean;
}

export const usePageMetaData = (
	content: BuilderContent | null | undefined,
	settings: SettingsResponse | undefined,
	hreflangUrls?: HreflangUrl[],
	options?: UsePageMetaDataOptions,
): PageMetadata | undefined => {
	const imageUrl = useDamFile(content?.data?.openGraphImageDam ?? null);
	const hreflangs = getHreflangs(hreflangUrls, settings);
	const location = useLocation();
	const { url: pageOrigin } = useContext(SegmentationContext);
	const fallbackCanonicalLink = new URL(location.pathname, pageOrigin).href;

	if (!content) return undefined;

	const noIndex = content.data?.noIndex;
	const canonicalLink = options?.skipCanonicalFallback
		? undefined
		: content.data?.canonicalLink || (noIndex ? undefined : fallbackCanonicalLink);

	return {
		title: content.data?.title,
		description: content.data?.description,
		canonicalLink,
		keywords: content.data?.keywords,
		noIndex: noIndex,
		noFollow: content.data?.noFollow,
		openGraph: {
			url: content.data?.openGraphUrl,
			title: content.data?.openGraphTitle ?? content.data?.title,
			description: content.data?.openGraphDescription ?? content.data?.description,
			image: imageUrl?.size960?.url ?? content.data?.openGraphImage,
			type: 'Website',
		},
		hreflangs: hreflangs,
	};
};
